import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

interface EditTitleModalProps {
  isOpen: boolean;
  currentTitle: string;
  onClose: () => void;
  onSave: (newTitle: string) => Promise<void>;
  heading: string;
  inputType?: string;
  isLoading?: boolean;
}

const EditTitleModal: React.FC<EditTitleModalProps> = ({
  isOpen,
  currentTitle,
  onClose,
  onSave,
  heading = "Template",
  inputType = "",
  isLoading = false,
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setTitle(currentTitle);
  }, [currentTitle, isOpen]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!title.trim()) return;

    setIsSaving(true);
    try {
      await onSave(title);
      onClose();
    } catch (error) {
      console.error("Error saving title:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full bg-black bg-opacity-50">
      <div className="w-full max-w-sm p-6 mx-4 bg-white dark:bg-gray-900 dark:text-white rounded-lg shadow-lg">
        <h2 className="mb-4 text-xl font-bold">
          Edit {heading} {heading === "Template" ? "Title" : ""}
        </h2>
        <div className="mb-4">
          {inputType !== "text" ? (
            <Input
              placeholder={`Edit ${heading} ${
                heading === "Template" ? "Title" : ""
              }`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full dark:text-white border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 focus:bg-white dark:focus:bg-gray-950"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                }
              }}
            />
          ) : (
            <Textarea
              placeholder={`Edit ${heading} ${
                heading === "Template" ? "Title" : ""
              }`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full dark:text-white border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 focus:bg-white dark:focus:bg-gray-950"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSave();
                }
              }}
            />
          )}
        </div>
        <div className="flex justify-end space-x-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="px-4 py-2 font-medium"
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading || !title.trim()}
            className="px-4 py-2 font-medium bg-sky-200 hover:bg-sky-100 text-white"
          >
            {isSaving || isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditTitleModal;
